﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import type {
  LoginRequest,
  OrganizerSessionResponse,
  ResendVerificationRequest,
  SignupRequest,
  SignupResponse,
  UpdateOrganizerProfileRequest,
  VerifyEmailRequest,
} from "@ticketnjoy/types";

import { ApiError, apiClient } from "@/lib/api-client";

export const meQueryKey = ["organizer", "me"] as const;

/**
 * GET /organizers/me → mevcut organizer + shops.
 * 401 durumunda hata ApiError olarak fırlar, retry yapılmaz (auth yok anlamına gelir).
 */
export function useMeQuery() {
  return useQuery<OrganizerSessionResponse, ApiError>({
    queryKey: meQueryKey,
    queryFn: () => apiClient<OrganizerSessionResponse>("/organizers/me"),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * `/me` 401 dönerse login sayfasına yönlendiren hook.
 * Dashboard layout'unda kullanılır.
 */
export function useAuthenticatedMe() {
  const navigate = useNavigate();
  const query = useMeQuery();

  useEffect(() => {
    if (query.error instanceof ApiError && query.error.status === 401) {
      navigate("/login", { replace: true });
    }
  }, [query.error, navigate]);

  return query;
}

export function useSignupMutation() {
  return useMutation<SignupResponse, Error, SignupRequest>({
    mutationFn: (body) =>
      apiClient<SignupResponse>("/organizers/signup", {
        method: "POST",
        body,
      }),
  });
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, Error, LoginRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/login", {
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

export function useVerifyEmailMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, Error, VerifyEmailRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/verify-email", {
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

export function useResendVerificationMutation() {
  return useMutation<void, Error, ResendVerificationRequest>({
    mutationFn: (body) =>
      apiClient<void>("/organizers/resend-verification", {
        method: "POST",
        body,
      }),
  });
}

/**
 * PATCH /organizers/me — organizer profil güncelleme. v1'de sadece logo.
 * Başarılı olunca `meQueryKey` cache'ini dönen response ile tazelerim; sidebar vb.
 * logo değişimini anında görür.
 */
export function useUpdateMeMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, ApiError, UpdateOrganizerProfileRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/me", {
        method: "PATCH",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => apiClient<void>("/organizers/logout", { method: "POST" }),
    onSuccess: () => {
      qc.clear();
    },
  });
}
